import { DateTime } from 'luxon';

/**
 * Convert a local datetime string to UTC ISO string
 * @param {string} localDateTime - Local datetime string from datetime-local input
 * @returns {string} UTC ISO string
 */
export const localToUTC = (localDateTime) => {
  if (!localDateTime) return null;
  const local = DateTime.fromISO(localDateTime);
  return local.toUTC().toISO();
};

/**
 * Convert a UTC ISO string to local datetime string for editing
 * @param {string} utcDateTime - UTC ISO string
 * @returns {string} Local datetime string for datetime-local input
 */
export const utcToLocal = (utcDateTime) => {
  if (!utcDateTime) return null;
  const utc = DateTime.fromISO(utcDateTime);
  return utc.toLocal().toFormat('yyyy-MM-dd\'T\'HH:mm');
};

/**
 * Format a UTC datetime for display in local timezone
 * @param {string} utcDateTime - UTC ISO string
 * @param {string} format - Luxon format string
 * @returns {string} Formatted local time string
 */
export const formatLocalTime = (utcDateTime, format = 'EEEE, MMMM dd, yyyy h:mm a') => {
  if (!utcDateTime) return '';
  const utc = DateTime.fromISO(utcDateTime);
  return utc.toLocal().toFormat(format);
};

/**
 * Get the user's current timezone
 * @returns {string} Timezone identifier (e.g., "America/New_York")
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Get timezone abbreviation for display
 * @param {string} utcDateTime - UTC ISO string
 * @returns {string} Timezone abbreviation (e.g., "EST", "PST")
 */
export const getTimezoneAbbr = (utcDateTime) => {
  if (!utcDateTime) return '';
  const utc = DateTime.fromISO(utcDateTime);
  return utc.toLocal().toFormat('ZZ');
};

/**
 * Display both local and UTC times for clarity
 * @param {string} utcDateTime - UTC ISO string
 * @returns {string} Formatted string showing both times
 */
export const displayBothTimes = (utcDateTime) => {
  if (!utcDateTime) return '';
  const utc = DateTime.fromISO(utcDateTime);
  const local = utc.toLocal();
  
  const localFormatted = local.toFormat('h:mm a');
  const utcFormatted = utc.toFormat('h:mm a');
  const tzAbbr = local.toFormat('ZZ');
  
  return `${localFormatted} ${tzAbbr} (${utcFormatted} UTC)`;
};

/**
 * Check if a game has started based on its UTC datetime
 * @param {string} utcDateTime - UTC ISO string of the game start time
 * @returns {boolean} True if the game has started, false otherwise
 */
export const hasGameStarted = (utcDateTime) => {
  if (!utcDateTime) return false;
  const gameStartTime = DateTime.fromISO(utcDateTime);
  const now = DateTime.utc();
  return now >= gameStartTime;
};

/**
 * Check if a game is within the submission window (before start time)
 * @param {string} utcDateTime - UTC ISO string of the game start time
 * @returns {boolean} True if picks can still be submitted, false otherwise
 */
export const canSubmitPicks = (utcDateTime) => {
  return !hasGameStarted(utcDateTime);
};
